import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import {
  RegistroFincaBorrador,
  RegistroFincaBorradorLote,
} from "../../../../../modulos/terceros/wizard/response/RegistroFincaBorrador";
import { parseWizardErrorMessage } from "../../../../../modulos/terceros/wizard/response/WizardValidationErrorResponse";
import {
  ActualizacionLoteTemp,
  registroFincaTempGrpcService,
} from "../../../../../modulos/terceros/wizard/services/RegistroFincaTempGrpcService";
import { LoteAssignMapRender } from "../map/LoteAssignMapRender";
import { LoteAssignEntry, LoteAssignState } from "../map/LoteAssignState";
import {
  BaseRegistroFincaPasoRender,
  RegistroFincaFooterOptions,
} from "./BaseRegistroFincaPasoRender";
import template from "./registroFincaPaso5Template.html";

/** Paso 5 del wizard de registro: asignar área productiva y cultivo (o
 *  cobertura, si no produce) a cada lote dividido en el paso 4
 *  (`LoteAssignState`/`LoteAssignMapRender`/`LoteAssignBarRender`).
 *
 *  **Guardado progresivo.** Cada "Aplicar" guarda la asignación de los lotes
 *  elegidos en el registro temporal (`ActualizarLotes`), en orden. Si alguno
 *  de esos guardados falla, no se pierde: "Continuar" reenvía la asignación
 *  de TODOS los lotes antes de confirmar el paso, así el servidor queda
 *  igual a la pantalla.
 *
 *  "Continuar" exige que no quede ningún lote sin asignar: el guardado final
 *  lo rechaza igual (`REGISTRO_INCOMPLETO`), y es mejor avisarlo acá, donde
 *  se corrige.
 *
 *  Los lotes (con lo ya asignado) se leen de la base al montar
 *  (`leerAsync`); no se reciben en memoria desde el paso 4. */
export class RegistroFincaPaso5Render extends BaseRegistroFincaPasoRender {
  private readonly _state = new LoteAssignState();
  private _mapRender: LoteAssignMapRender | null = null;
  /** Guardados de "Aplicar", encadenados para que lleguen en orden. */
  private _colaGuardado: Promise<void> = Promise.resolve();
  private _guardando = false;
  private _disposed = false;

  constructor(
    private readonly _borrador: RegistroFincaBorrador,
    private readonly _onVolver: () => void,
    private readonly _onAsignacionLista: (
      borrador: RegistroFincaBorrador,
    ) => void,
  ) {
    super(_borrador.clienteId);
  }

  renderBody(): string {
    return template;
  }

  connectBody(): void {
    registroFincaTempGrpcService
      .leerAsync(this._borrador)
      .then((leido) => {
        if (this._disposed) return;
        if (leido.lotes.length === 0) {
          throw new Error("La finca no tiene lotes guardados. Vuelva al paso 4.");
        }
        this._montar(leido.lotes);
      })
      .catch((error) => {
        serviceProvider
          .getErrorService()
          .addError("Error al leer los lotes", parseWizardErrorMessage(error));
      });
  }

  private _montar(lotes: RegistroFincaBorradorLote[]): void {
    this._state.init(lotes);
    this._state.addOnAsignacionAplicadaListener(this._guardarAsignacion);

    const mapMount = document.getElementById("registroFincaPaso5MapMount");
    this._mapRender = new LoteAssignMapRender(
      this._state,
      this._borrador.tipo === 0,
      // Ver el comentario del paso 4: sólo en edición.
      this._borrador.esEdicion,
    );
    if (mapMount) {
      mapMount.innerHTML = this._mapRender.render();
      this._mapRender.connect();
    }
  }

  disposeBody(): void {
    this._disposed = true;
    this._mapRender?.dispose();
  }

  getFooterOptions(): RegistroFincaFooterOptions {
    return {
      volver: { onClick: this._onVolverClick },
      submit: { label: "Continuar →", onClick: this._onSubmitClick },
    };
  }

  /** "Aplicar": guarda la asignación de esos lotes en el servidor. */
  private _guardarAsignacion = (entries: LoteAssignEntry[]): void => {
    const registroId = this._borrador.registroId;
    const actualizaciones = aActualizaciones(entries);
    if (!registroId || actualizaciones.length === 0) return;

    this._colaGuardado = this._colaGuardado
      .then(() =>
        registroFincaTempGrpcService.actualizarLotesAsync(
          registroId,
          actualizaciones,
        ),
      )
      .catch((error) => {
        serviceProvider
          .getErrorService()
          .addError(
            "Error al guardar la asignación",
            parseWizardErrorMessage(error),
          );
      });
  };

  /** "Volver" espera los guardados pendientes: el paso al que se vuelve lee
   *  de la base. */
  private _onVolverClick = (): void => {
    this._colaGuardado.then(() => this._onVolver());
  };

  private _onSubmitClick = (): void => {
    const registroId = this._borrador.registroId;
    if (this._guardando || !registroId) return;

    const sinAsignar =
      this._state.getTotalCount() - this._state.getTocadosCount();
    if (sinAsignar > 0) {
      serviceProvider
        .getErrorService()
        .addError(
          "Faltan lotes por asignar",
          sinAsignar === 1
            ? "Queda 1 lote sin asignar. Indique si es área productiva antes de continuar."
            : `Quedan ${sinAsignar} lotes sin asignar. Indique si son área productiva antes de continuar.`,
        );
      return;
    }

    this._guardando = true;
    const btnSubmit = document.getElementById(
      "registroFincaSubmit",
    ) as HTMLButtonElement | null;
    if (btnSubmit) btnSubmit.disabled = true;

    // Primero lo que quedó en cola; después, la asignación de todos (cubre
    // cualquier "Aplicar" que haya fallado) y la confirmación del paso.
    this._colaGuardado
      .then(() =>
        registroFincaTempGrpcService.actualizarLotesAsync(
          registroId,
          aActualizaciones(this._state.getAll()),
        ),
      )
      .then(() => registroFincaTempGrpcService.avanzarPasoAsync(registroId, 5))
      .then(() => {
        if (this._disposed) return;
        this._borrador.ultimoPaso = Math.max(this._borrador.ultimoPaso, 5);
        this._onAsignacionLista(this._borrador);
      })
      .catch((error) => {
        this._guardando = false;
        if (btnSubmit) btnSubmit.disabled = false;
        serviceProvider
          .getErrorService()
          .addError(
            "Error al guardar la asignación",
            parseWizardErrorMessage(error),
          );
      });
  };
}

function aActualizaciones(
  entries: readonly LoteAssignEntry[],
): ActualizacionLoteTemp[] {
  return entries
    .filter((entry) => entry.tempId)
    .map((entry) => ({
      id: entry.tempId!,
      asignacion: {
        isAreaProductiva: entry.isAreaProductiva,
        cultivoId: entry.cultivoId,
        coberturaId: entry.coberturaId,
      },
    }));
}
